import React, {useEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import {t} from '@di-shared/features/common/useInferableTranslation';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';

interface MonthlySafetyScoreChartEuProps {
  selectedMonth: string;
  onSelectedMonth?: (month: string) => void;
  data: {score: number; date: string}[];
}

enum SCORE_THRESHOLD {
  GOOD_CAUTION = 88,
  CAUTION_WARNING = 74,
}

export default function ({onSelectedMonth = () => {}, selectedMonth, data}: MonthlySafetyScoreChartEuProps) {
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(data.length < 7);

  function scrollToStart() {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    }
  }

  function scrollToEnd() {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: scrollRef.current.scrollWidth,
        behavior: 'smooth',
      });
    }
  }

  function handleScroll() {
    if (scrollRef.current) {
      const {scrollLeft, scrollWidth, clientWidth} = scrollRef.current;
      setIsAtStart(scrollLeft === 0);
      setIsAtEnd(scrollLeft + clientWidth >= scrollWidth);
    }
  }

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  useEffect(() => {
    if (data.length > 5) {
      scrollToEnd();
    }
  }, []);

  return (
    <div className="section_container">
      <div className="statistics_graph_scroll_wrap">
        <div
          className="bar_graph_average"
          style={{top: `${135 - data.reduce((acc, cur) => acc + cur.score, 0) / data.length}px`}}
        >
          <span className="average_line"></span>
        </div>

        <div
          className={clsx('statistics_graph_area graph_full_type')}
          ref={scrollRef}
          style={{
            scrollbarWidth: 'none' /* Firefox */,
            msOverflowStyle: 'none' /* Internet Explorer and Edge */,
          }}
        >
          <div
            className={clsx(`bar_graph`, {is_center: data.length < 6})}
            style={{minWidth: 48 * data.length}}
          >
            {data.map(({score, date}) => {
              const parsedData = parseDateWithFormat(date);
              if (!parsedData) {
                return <></>;
              }
              return (
                <div
                  className={clsx('bar_graph_item', {
                    'is-active': date === selectedMonth,
                    type_warning: score <= SCORE_THRESHOLD.CAUTION_WARNING,
                    type_caution: score <= SCORE_THRESHOLD.GOOD_CAUTION,
                    type_good: score > SCORE_THRESHOLD.GOOD_CAUTION,
                  })}
                  key={date}
                  onClick={() => {
                    onSelectedMonth(date);
                  }}
                >
                  {date === selectedMonth && <span className="speech_bubble">{score}</span>}
                  <span
                    className="graph_bar"
                    // 최대 높이 90px <-> 최대 점수 100점
                    style={{height: `${0.9 * score}px`}}
                  ></span>
                  <span className="graph_title">{CldrDatetimeFormatter.format(parsedData, 'yMMM')}</span>
                </div>
              );
            })}
          </div>
        </div>
        {!isAtStart && (
          <button
            type="button"
            className="btn_prev"
            onClick={() => scrollToStart()}
          >
            <span className="ic ic_arrow_back_16px">
              <span className="blind">{t('T200006')}</span>
            </span>
          </button>
        )}
        {!isAtEnd && (
          <button
            type="button"
            className="btn_next"
            onClick={() => scrollToEnd()}
          >
            <span
              className="ic ic_arrow_forward_16px"
              aria-hidden="true"
            >
              <span className="blind">{t('ctn_dss_monthly_chart_next')}</span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
}
